import { useCallback } from 'react'

export interface Message {
  role: 'system' | 'user' | 'assistant'
  content: string
}

interface UseAthena {
  streamChat: (
    messages: Message[],
    onChunk: (content: string) => void,
    onDone: () => void,
    onError: (error: Error) => void,
  ) => Promise<void>
  generateText: (systemPrompt: string, userPrompt: string) => Promise<string>
}

const PROXY_URL = 'https://www.requests.magicpatterns.com/openai/proxy'

export function useAthena(): UseAthena {
  const streamChat = useCallback(
    async (
      messages: Message[],
      onChunk: (content: string) => void,
      onDone: () => void,
      onError: (error: Error) => void,
    ) => {
      try {
        const response = await fetch(PROXY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: 'gpt-4-turbo',
            messages,
            stream: true,
            temperature: 0.25,
            max_tokens: 2500,
          }),
        })

        if (!response.ok) {
          throw new Error(
            response.status >= 500
              ? 'Athena is temporarily unavailable. Please try again.'
              : `Athena error: ${response.status}`,
          )
        }

        if (!response.body) {
          throw new Error('No response body')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            const trimmed = line.trim()
            if (!trimmed || !trimmed.startsWith('data: ')) continue
            const data = trimmed.slice(6)
            if (data === '[DONE]') {
              onDone()
              return
            }
            try {
              const json = JSON.parse(data)
              const content = json.choices?.[0]?.delta?.content
              if (content) onChunk(content)
            } catch {}
          }
        }

        onDone()
      } catch (error) {
        console.error('Athena stream error:', error)
        onError(error instanceof Error ? error : new Error('Unknown error'))
      }
    },
    [],
  )

  const generateText = useCallback(
    async (systemPrompt: string, userPrompt: string): Promise<string> => {
      const response = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt },
          ],
          stream: false,
          temperature: 0.25,
          max_tokens: 2500,
        }),
      })

      if (!response.ok) {
        throw new Error(
          response.status >= 500
            ? 'Athena is temporarily unavailable. Please try again.'
            : `Athena error: ${response.status}`,
        )
      }

      const data = await response.json()
      return data.choices?.[0]?.message?.content || ''
    },
    [],
  )

  return { streamChat, generateText }
}
